/**
 * Where a ray meets the ground.
 *
 * This is the root of the whole perception surface: `main.ts` scores every
 * candidate the pointer could mean as `distance / tolerance` from the point
 * this returns, so a metre of error here is a metre of error under *every*
 * subject in the world at once.
 *
 * It used to live inline in main.ts and march `region.heightAt` alone — the
 * coarse 260 m bicubic — lifting whatever it found onto the true surface at
 * the end, with the note *"the detail it skips is a couple of metres of
 * roughness ... so nothing downstream can tell"*. Both halves of that were
 * wrong, and `tools/pick.ts` prices them:
 *
 *   - the two surfaces disagree by **p50 7.4 m, p90 15.1 m**, not a couple;
 *   - lifting fixes the point's HEIGHT and cannot fix its HORIZONTAL
 *     POSITION, which is the only thing anything is ever scored against.
 *
 * A shallow ray multiplies the vertical disagreement by `1/tan(descent)` —
 * 2.14 at 25° — so aiming squarely at a person put the reference point a
 * median **15.3 m** away against a **7.1 m** tolerance, and **only 34% of
 * aims taken directly at a subject landed near enough to offer it at all.**
 * Nothing downstream could tell because nothing downstream was asked; the
 * failure looks exactly like "the pointer is fiddly".
 *
 * So the march keeps the cheap field to *bracket* the crossing and then
 * resolves it against the surface things actually stand on. The expensive
 * height is sampled a few dozen times near the answer rather than a few
 * hundred times along the ray, which is what made the shortcut attractive in
 * the first place (a full fine march measured 2.5 ms a pick).
 *
 * Pure, and it takes its two height fields as callbacks: `src/sim/` never
 * calls it, `tools/` and `test/` call it with the same functions main.ts
 * does, so the probe cannot drift from the game.
 */

export interface AimPoint { x: number; y: number; z: number }

/** Halvings of the final bracket. 14 puts a 50 m bracket inside 3 mm. */
const BISECT = 14;

/**
 * How far along the ray the refinement will walk looking for a bracket,
 * as a multiple of the largest vertical disagreement it might have to undo.
 * `surfaceHeight`'s detail budget is ±28 m; a ray at 20° converts that into
 * 82 m of travel, so 3× the coarse crossing's own step schedule is generous
 * without being a second march.
 */
const WALK_STEPS = 48;

/**
 * March `o + d·t` to the ground, resolving against `trueAt`.
 *
 * @param coarseAt cheap height — one bicubic sample; used only to bracket
 * @param trueAt   the surface things stand on; the answer is a root of this
 * @param sky      above every peak, so a ray leaving upward can be refused
 */
export function marchToGround(
  o: AimPoint,
  d: AimPoint,
  coarseAt: (x: number, z: number) => number,
  trueAt: (x: number, z: number) => number,
  sky: number,
): AimPoint | null {
  // looking up from above the mountains, there is nothing to hit
  if (d.y >= 0 && o.y > sky) return null;

  /** Height of the ray above the TRUE surface at t. The root we want. */
  const gap = (t: number) => (o.y + d.y * t) - trueAt(o.x + d.x * t, o.z + d.z * t);

  if (gap(0) <= 0) return { ...o };

  // ---- 1. bracket on the cheap field -----------------------------------
  // Steps grow with distance: a metre of error at four kilometres is a
  // quarter of a pixel, and this pass only has to get us near.
  let coarseT = -1;
  {
    let t0 = 0;
    for (let t = 1; t < 60000;) {
      t += Math.max(2, t * 0.05);
      if ((o.y + d.y * t) - coarseAt(o.x + d.x * t, o.z + d.z * t) > 0) { t0 = t; continue; }
      let lo = t0, hi = t;
      for (let i = 0; i < 13; i++) {
        const m = (lo + hi) / 2;
        if ((o.y + d.y * m) - coarseAt(o.x + d.x * m, o.z + d.z * m) <= 0) hi = m; else lo = m;
      }
      coarseT = hi;
      break;
    }
  }
  if (coarseT < 0) return null;              // the ray leaves the world

  // ---- 2. bracket the TRUE crossing, starting from there ----------------
  // The two surfaces disagree, so the coarse answer may sit either side of
  // the real one. Walk the shorter way until the sign flips.
  const step = Math.max(2, coarseT * 0.02);
  let lo = -1, hi = -1;
  if (gap(coarseT) > 0) {
    // still airborne at the coarse crossing — the true ground is further on
    lo = coarseT;
    for (let i = 1; i <= WALK_STEPS; i++) {
      const t = coarseT + step * i;
      if (gap(t) <= 0) { hi = t; break; }
      lo = t;
    }
  } else {
    // already underground there — the true ground was earlier
    hi = coarseT;
    for (let i = 1; i <= WALK_STEPS; i++) {
      const t = Math.max(0, coarseT - step * i);
      if (gap(t) > 0) { lo = t; break; }
      hi = t;
      if (t === 0) break;
    }
  }

  // No bracket found: the fine surface never crosses within reach of the
  // coarse one. Fall back to the coarse answer rather than refusing the
  // pick — a displaced point is worth more than no point at all.
  if (lo < 0 || hi < 0) {
    const x = o.x + d.x * coarseT, z = o.z + d.z * coarseT;
    return { x, y: trueAt(x, z), z };
  }

  // ---- 3. bisect ---------------------------------------------------------
  for (let i = 0; i < BISECT; i++) {
    const m = (lo + hi) / 2;
    if (gap(m) <= 0) hi = m; else lo = m;
  }
  const x = o.x + d.x * hi, z = o.z + d.z * hi;
  return { x, y: trueAt(x, z), z };
}
